import React, { useState, useRef, useEffect, useContext, useMemo } from "react";
import {
  Building2,
  Zap,
  Users,
  UserCheck,
  Briefcase,
  Award,
  BookOpen,
  ShieldCheck,
  BarChart3,
  Plus,
} from "lucide-react";
import { DashboardContext } from "../contexts/DashboardContext";
import AddEmployeeModal from "./AddEmployeeModal";

export default function DashboardHeader() {
  const { employeesData, refreshEmployees, addEmployeeToState } =
    useContext(DashboardContext);
  const [showSummary, setShowSummary] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSummary(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const dynamicSummary = useMemo(() => {
    const data = employeesData || [];

    const uniqueDepartments = new Set();
    const uniqueTeams = new Set();
    const uniqueRoles = new Set();
    const uniqueDesignations = new Set();

    data.forEach((emp) => {
      if (emp.department) uniqueDepartments.add(emp.department);
      if (emp.team) uniqueTeams.add(emp.team);
      if (emp.remarks) uniqueRoles.add(emp.remarks);
      if (emp.designation) uniqueDesignations.add(emp.designation);
    });

    return [
      { label: "Departments", count: uniqueDepartments.size, icon: Building2 },
      { label: "Functions", count: 0, icon: Zap },
      { label: "Teams", count: uniqueTeams.size, icon: Users },
      { label: "Job Levels", count: 0, icon: UserCheck },
      { label: "Roles", count: uniqueRoles.size, icon: Briefcase },
      { label: "Designations", count: uniqueDesignations.size, icon: Award },
      { label: "Skills", count: 0, icon: BookOpen },
      { label: "Certifications", count: 0, icon: ShieldCheck },
    ];
  }, [employeesData]);

  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 relative w-full">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight">
          Employee Dashboard
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          Overview of employee data and key insights
        </p>
      </div>

      <div className="flex items-center gap-3 relative" ref={dropdownRef}>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-xs font-semibold shadow-sm transition-colors whitespace-nowrap cursor-pointer"
        >
          <Plus className="w-4 h-4" />
          Add Employee
        </button>

        <button
          onClick={() => setShowSummary(!showSummary)}
          className="flex items-center gap-2 bg-white hover:bg-slate-50 text-blue-600 border border-slate-200 px-4 py-2.5 rounded-xl text-xs font-semibold shadow-sm transition-colors whitespace-nowrap cursor-pointer"
        >
          <BarChart3 className="w-4 h-4 text-blue-600" />
          Employee Summary
        </button>

        {showSummary && (
          <div className="absolute right-0 top-14 w-72 md:w-80 bg-white rounded-2xl shadow-xl border border-slate-100 p-5 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
            <h3 className="text-sm font-bold text-slate-900 mb-4 tracking-tight">
              Employee Summary
            </h3>

            <div className="flex flex-col divide-y divide-slate-100">
              {dynamicSummary.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div
                    key={index}
                    className="py-3 flex items-center justify-between text-xs transition-colors hover:bg-slate-50/50 rounded-lg px-2 -mx-2"
                  >
                    <div className="flex items-center gap-3 text-slate-600 font-medium">
                      <IconComponent className="w-4 h-4 text-slate-400" />
                      <span>{item.label}</span>
                    </div>
                    <span className="font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded-md">
                      {item.count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Render the Add Employee Modal Component */}
      <AddEmployeeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onEmployeeAdded={(newEmp) => {
          console.log("Employee added successfully:", newEmp);
          if (addEmployeeToState) {
            addEmployeeToState(newEmp); // Instantly updates table state & session cache
          }
          refreshEmployees();
        }}
      />
    </div>
  );
}
